/**
 * Kader AI Design Tokens
 * ──────────────────────
 * Extracted pixel-for-pixel from 15 interactive .jsx mockups.
 * Source of truth for all colors, typography, spacing, and semantic tokens.
 *
 * RULES:
 * - No emoji anywhere in the UI
 * - No AI imagery — human SVG icons only
 * - Orange accent used sparingly — never for large fills
 * - DM Mono for all data, timestamps, labels
 * - DM Sans for all prose and headings
 */

// ─── CORE PALETTE ────────────────────────────────────────────────────────────

export const colors = {
  // Backgrounds (darkest → lightest)
  bgPrimary: '#0A0A0A',
  bgSecondary: '#0F0F0F',
  bgCard: '#141414',
  bgCardHover: '#1A1A1A',
  bgCoach: '#161616',       // Coach chat bubble background
  bgUser: '#1C1708',        // User chat bubble background (warm tint)

  // Text hierarchy
  textPrimary: '#F2F2F2',
  textSecondary: '#8A8A8A',
  textMuted: '#808080',   // ≥4.5:1 on bgCard — was #5A5A5A (2.7:1, WCAG AA fail)

  // Borders
  border: '#1E1E1E',

  // Accent — used sparingly
  accent: '#E8731A',

  // Semantic colors
  green: '#4A8C5C',         // Success, on-target, active, approved
  yellow: '#C4A24E',        // Warning, moderate effort, fast
  red: '#E8593C',           // Error, hard effort, ceiling, danger
  blue: '#3B8BD4',          // Session 2, Pro tier, informational
  purple: '#8B6CC1',        // Trial tier, special states
  cyan: '#5BA8C8',          // Morning routine, injury prevention

  // Heart rate specific
  hrRed: '#E8593C',         // HR display, heart icon
} as const;

// ─── SEMANTIC ALIASES ────────────────────────────────────────────────────────

export const effort = {
  hard: colors.red,
  moderate: colors.yellow,
  easy: colors.green,
  veryEasy: colors.green,
  rest: colors.textMuted,
} as const;

export const score = {
  high: colors.accent,      // >= 8
  medium: colors.yellow,    // >= 6
  low: colors.green,        // < 6
} as const;

export const tier = {
  elite: colors.accent,
  pro: colors.blue,
  basic: colors.textMuted,
  trial: colors.purple,
} as const;

export const status = {
  active: colors.green,
  trial: colors.purple,
  churned: colors.red,
} as const;

// ─── TYPOGRAPHY ──────────────────────────────────────────────────────────────

export const font = {
  sans: "'DM Sans', 'Helvetica Neue', sans-serif",
  mono: "'DM Mono', monospace",
} as const;

export const fontSize = {
  // Labels & metadata
  xxs: '9px',      // Section labels, sub-text
  xs: '10px',      // Timestamps, tab labels, tertiary info
  sm: '11px',      // Secondary labels, descriptions
  // Body & UI
  base: '12px',    // List items, card descriptions
  md: '13px',      // Body text, coach suggestions
  lg: '14px',      // Primary body, input text, coach prose
  xl: '15px',      // Card titles, button text
  // Headings
  '2xl': '16px',   // Pace display, sub-headings
  '3xl': '17px',   // Screen sub-titles
  '4xl': '18px',   // Tier names, section titles
  '5xl': '20px',   // Score numbers
  '6xl': '22px',   // Stat values
  '7xl': '24px',   // Screen titles (mobile)
  '8xl': '26px',   // Morning brief h1
  '9xl': '28px',   // Desktop headings, large display
  hero: '32px',    // Landing CTA
  display: '48px', // Landing hero
} as const;

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const lineHeight = {
  tight: 1.15,
  snug: 1.2,
  normal: 1.4,
  relaxed: 1.5,
  body: 1.6,
  prose: 1.65,
  spacious: 1.7,
} as const;

export const letterSpacing = {
  tight: '-0.02em',
  snug: '-0.01em',
  normal: '0',
  wide: '0.03em',
  wider: '0.05em',
  label: '0.1em',
  section: '0.12em',
  heading: '0.15em',
  spread: '0.2em',
} as const;

// ─── SPACING ─────────────────────────────────────────────────────────────────

export const spacing = {
  0: '0px',
  1: '2px',
  2: '4px',
  3: '6px',
  4: '8px',
  5: '10px',
  6: '12px',
  7: '14px',
  8: '16px',
  9: '18px',
  10: '20px',
  11: '24px',
  12: '28px',
  14: '32px',
  16: '36px',
  18: '40px',
  20: '48px',
  24: '60px',
  28: '80px',
} as const;

// ─── RADII ───────────────────────────────────────────────────────────────────

export const radius = {
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '10px',
  '2xl': '12px',
  '3xl': '14px',
  '4xl': '16px',
  '5xl': '18px',
  '6xl': '20px',
  pill: '9999px',
  circle: '50%',
} as const;

// ─── LAYOUT CONSTANTS ────────────────────────────────────────────────────────

export const layout = {
  mobileMaxWidth: 430,       // px — all mobile screens
  desktopMinWidth: 1024,     // px — desktop breakpoint
  pagePaddingMobile: 24,     // px
  pagePaddingDesktop: 32,    // px
  bottomNavHeight: 68,       // px (12 + 20icon + 4gap + 10label + 22safeArea)
  statusBarHeight: 44,       // px (iOS safe area)
  headerHeight: 56,          // px
} as const;

// ─── TRANSITIONS ─────────────────────────────────────────────────────────────

export const transition = {
  fast: '0.15s ease',
  base: '0.2s ease',
  slow: '0.3s ease',
  spring: '0.4s ease',
  smooth: '0.5s ease',
} as const;

// ─── SHADOWS ─────────────────────────────────────────────────────────────────

export const shadow = {
  none: 'none',
  sm: '0 1px 3px rgba(0,0,0,0.3)',
  md: '0 4px 12px rgba(0,0,0,0.3)',
  lg: '0 8px 24px rgba(0,0,0,0.4)',
  blur: '20px',              // backdrop-filter blur value
} as const;

// ─── BACKDROP ────────────────────────────────────────────────────────────────

export const backdrop = {
  nav: `${colors.bgPrimary}E8`,       // ~91% opacity
  sheet: `${colors.bgPrimary}F0`,     // ~94% opacity
  input: `${colors.bgPrimary}F5`,     // ~96% opacity
} as const;

// ─── Z-INDEX ─────────────────────────────────────────────────────────────────

export const zIndex = {
  base: 0,
  card: 1,
  sticky: 10,
  overlay: 20,
  modal: 30,
  toast: 40,
} as const;

// Re-export theme color sets so tokens.ts is the single import for tokens
export { darkColors, lightColors } from '@/lib/theme-context';
export type { ThemeColors } from '@/lib/theme-context';

// Daily bib tokens — its own paper/ink surface, see ./bib.ts
export { bibColors, bibFont, bibType, bibWeight, bibSpace, bibReference } from './bib';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/** Add alpha to a hex color: alpha('#E8731A', '15') → '#E8731A15' */
export function alpha(hex: string, opacity: string): string {
  return `${hex}${opacity}`;
}

/** Get effort color from effort level string */
export function effortColor(effortLevel: string): string {
  if (effortLevel === 'Hard') return effort.hard;
  if (effortLevel === 'Moderate') return effort.moderate;
  if (effortLevel === 'Easy' || effortLevel === 'Very Easy') return effort.easy;
  return colors.textSecondary;
}

/** Get score color from numeric score */
export function scoreColor(s: number): string {
  if (s >= 8) return score.high;
  if (s >= 6) return score.medium;
  return score.low;
}

/** Get tier color from tier name */
export function tierColor(t: string): string {
  const key = t.toLowerCase() as keyof typeof tier;
  return tier[key] ?? colors.textMuted;
}
